import {
  styled,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import LibMarkdown, { Options } from "react-markdown";
import remarkGfm from "remark-gfm";

// Styled components for markdown rendering (migrated from hendren/Markdown.tsx)
const StyledTableCell = styled(TableCell)(({ theme }) => ({
  fontSize: theme.typography.body1.fontSize,
  padding: theme.spacing(0, 0.5),
  "&:first-of-type": {
    paddingLeft: theme.spacing(0),
  },
  "&:last-of-type": {
    paddingRight: theme.spacing(0),
  },
  border: "none",
}));

const TypographyBold = styled(Typography)(({ theme }) => ({
  fontWeight: theme.typography.h1.fontWeight,
}));

// Remove excessive top and bottom margins for first and last paragraph in Markdown block
const StyledParagraph = styled(Typography)(({ theme }) => ({
  "div > &:first-of-type": {
    marginTop: theme.spacing(0),
  },
  "div > &:last-of-type": {
    marginBottom: theme.spacing(0),
  },
}));

// Markdown component definitions (migrated from hendren/Markdown.tsx)
export const markdownComponents: Options["components"] = {
  table(props) {
    return <Table size="small">{props.children}</Table>;
  },
  th(props) {
    return <StyledTableCell>{props.children}</StyledTableCell>;
  },
  tr(props) {
    return <TableRow>{props.children}</TableRow>;
  },
  td(props) {
    return <StyledTableCell>{props.children}</StyledTableCell>;
  },
  tbody(props) {
    return <TableBody>{props.children}</TableBody>;
  },
  thead(props) {
    return <TableHead>{props.children}</TableHead>;
  },
  li(props) {
    return <li style={{ listStyleType: "inherit" }}>{props.children}</li>;
  },
  h1(props) {
    return (
      <TypographyBold as="h1" variant="h1">
        {props.children}
      </TypographyBold>
    );
  },
  h2(props) {
    return (
      <TypographyBold as="h2" variant="h2">
        {props.children}
      </TypographyBold>
    );
  },
  h3(props) {
    return (
      <TypographyBold as="h3" variant="h3">
        {props.children}
      </TypographyBold>
    );
  },
  h4(props) {
    return (
      <TypographyBold as="h4" variant="h4">
        {props.children}
      </TypographyBold>
    );
  },
  h5(props) {
    return (
      <TypographyBold as="h5" variant="h5">
        {props.children}
      </TypographyBold>
    );
  },
  h6(props) {
    return (
      <TypographyBold as="h6" variant="h6">
        {props.children}
      </TypographyBold>
    );
  },
  strong(props) {
    return <TypographyBold as="strong">{props.children}</TypographyBold>;
  },
  p(props) {
    return <StyledParagraph variant="body1">{props.children}</StyledParagraph>;
  },
};

export const Markdown = (props: { children: string }) => {
  const processContent = (content: string): string => {
    if (!content) return content;

    // Handle common escaped characters that might appear in JSON strings
    return content
      .replace(/\\\\/g, "\\")
      .replace(/\\n/g, "\n")
      .replace(/\\t/g, "\t")
      .replace(/\\r/g, "\r");
  };

  const processedContent = processContent(props.children);

  return (
    <LibMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
      {processedContent}
    </LibMarkdown>
  );
};
